import * as React from 'react';
import { createContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getCurrentUser,
  fetchAuthSession,
  signIn,
  signOut,
  confirmSignIn,
  type AuthSession
} from 'aws-amplify/auth';

import { useTranslation } from 'react-i18next';

import { CognitoUserExt } from '../login/cognitoUser';
import useNotify from '../utils/hooks/useNotify';
import useErrorMessage from '../utils/hooks/useErrorMessage';

type AuthContext = {
  isLoggedIn: boolean | undefined;
  login: (username: string, password: string) => Promise<void>;
  logout: (callback?: () => void) => void;
  refreshSession: () => Promise<string>;
  completeNewPassword: (password: string) => Promise<void>;
  user: CognitoUserExt | undefined;
  userSession: AuthSession | null;
  token: string;
};

const AuthContext = createContext<AuthContext>({
  isLoggedIn: undefined,
  login: () => Promise.reject(),
  logout: () => null,
  refreshSession: () => Promise.reject(),
  completeNewPassword: () => Promise.reject(),
  user: undefined,
  userSession: null,
  token: '',
});

  interface Props {
    children?: React.ReactNode;
  }

  const AuthProvider: React.FC<Props> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>();
  const [user, setUser] = useState<CognitoUserExt>();
  const [token, setToken] = useState('');
  const [userSession, setUserSession] = useState<AuthSession | null>(
    null
  );
  const notify = useNotify();

  const { t: _t } = useTranslation();
  const { errorMessage } = useErrorMessage();
  const history = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  // Set session data etc
  const afterLogin = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      setUserSession(session);
      setToken(session.tokens?.idToken?.toString() || '');
    } catch (error) {
      console.error('Error fetching session', error);
    }
  }, []);

  // On mount, check if user data exists locally then autologin
  useEffect(() => {
    getCurrentUser()
      .then(async (res) => {
        if (res) {
          setIsLoggedIn(true);
          setUser(res as CognitoUserExt);
          await afterLogin();
        }
      })
      .catch(() => {
        setIsLoggedIn(false);
        if (pathname !== '/login') {
            history('/login');
        }
      });
  }, [afterLogin, history, pathname]);

  // Refresh current session
  const refreshSession = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const newToken = session.tokens?.idToken?.toString() || '';
      setToken(newToken);
      setUserSession(session);
      return newToken;
    } catch {
      return '';
    }
  }, []);

  // Refresh session when location changes
  useEffect(() => {
    refreshSession();
  }, [refreshSession, pathname]);

  // Login
  const login = useCallback(
    async (username: string, password: string) => {
      try {
        const { isSignedIn, nextStep } = await signIn({ username, password });
        
        if (isSignedIn) {
          setIsLoggedIn(true);
          const currentUser = await getCurrentUser();
          setUser(currentUser as CognitoUserExt);
          await afterLogin();
        } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
             // Handle next step (e.g. set user state to show new password form)
             const currentUser = await getCurrentUser();
             setUser(currentUser as CognitoUserExt);
        }
      } catch (error) {
        errorMessage({
          ns: 'auth',
          error: error as Error,
          defaultTranslation: 'generic',
          cases: [
            {
              text: 'does not exist',
              translation: 'notFound',
            },
            {
              text: 'incorrect username or password',
              translation: 'incorrect',
            },
          ],
        });
      }
    },
    [afterLogin, errorMessage]
  );

  // Complete new password
  const completeNewPassword = useCallback(
    async (password: string) => {
      try {
        const { isSignedIn } = await confirmSignIn({ challengeResponse: password });
        if (isSignedIn) {
            setIsLoggedIn(true);
            const currentUser = await getCurrentUser();
            setUser(currentUser as CognitoUserExt);
            await afterLogin();
        }
      } catch (err: any) {
        notify({
          message: err?.message || err,
          type: 'error',
        });
      }
    },
    [afterLogin, notify]
  );

  // Logout
  const logout = useCallback(
    async (cb?: () => void) => {
      try {
        await signOut();
        setIsLoggedIn(false);
        setUser(undefined);
        setUserSession(null);
        setToken('');
        if (cb) {
          cb();
        }
      } catch (err) {}
    },
    []
  );

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        login,
        logout,
        refreshSession,
        user,
        userSession,
        completeNewPassword,
        token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext };
